// =====================================================================
// PatientFinance (Financeiro F1.1 / S58) — painel financeiro DENTRO da ficha
// do paciente (aba do PatientDetail). View dos orçamentos / contas a receber /
// pagamentos DAQUELE paciente + ação Receber + recibo PDF.
// Reusa financeService + PaymentDialog do módulo /financas (FinanceiroClinica).
// =====================================================================
import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Box
} from "@material-ui/core";
import PaymentIcon from "@material-ui/icons/Payment";
import ReceiptIcon from "@material-ui/icons/Receipt";
import { toast } from "react-toastify";
import {
  listReceivables,
  listQuotes,
  listPayments,
  ensureReceipt,
  openReceiptPdf,
  fmtMoney,
  RECEIVABLE_STATUS,
  QUOTE_STATUS,
  financeErrorMessage
} from "../../services/financeService";
import PaymentDialog from "../FinanceiroClinica/PaymentDialog";

const useStyles = makeStyles((theme) => ({
  section: { marginBottom: theme.spacing(3) },
  sectionTitle: { fontWeight: 600, marginBottom: theme.spacing(1) },
  loadingWrap: { display: "flex", justifyContent: "center", padding: theme.spacing(4) },
  empty: { color: theme.palette.text.secondary, padding: theme.spacing(1, 0) }
}));

const METHOD_LABELS = {
  dinheiro: "Dinheiro",
  pix: "PIX",
  debito: "Débito",
  credito: "Crédito",
  convenio: "Convênio",
  transferencia: "Transferência",
  cortesia: "Cortesia"
};

const formatDateBR = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  if (!y || !m || !d) return "—";
  return `${d}/${m}/${y}`;
};

const Status = ({ map, value, overdue }) => {
  const eff = overdue && (value === "pendente" || value === "parcial") ? "atrasado" : value;
  const cfg = (map && map[eff]) || { label: value, color: "#757575" };
  return (
    <Chip size="small" label={cfg.label} style={{ backgroundColor: cfg.color, color: "#fff", fontWeight: 600 }} />
  );
};

const PatientFinance = ({ patientId }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [receivables, setReceivables] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [payments, setPayments] = useState([]);
  const [payTarget, setPayTarget] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const [rcv, qts, pys] = await Promise.all([
        listReceivables({ patientId, pageSize: 100 }),
        listQuotes({ patientId, pageSize: 100 }),
        listPayments({ patientId, pageSize: 100 })
      ]);
      setReceivables(Array.isArray(rcv?.receivables) ? rcv.receivables : []);
      setQuotes(Array.isArray(qts?.quotes) ? qts.quotes : []);
      setPayments(Array.isArray(pys?.payments) ? pys.payments : []);
    } catch (e) {
      toast.error("Falha ao carregar o financeiro do paciente.");
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleReceipt = async (paymentId) => {
    try {
      const r = await ensureReceipt(paymentId);
      await openReceiptPdf(r.id);
    } catch (err) {
      toast.error(financeErrorMessage(err));
    }
  };

  const totalReceber = receivables
    .filter((r) => r.status === "pendente" || r.status === "parcial")
    .reduce((s, r) => s + (Number(r.remaining) || 0), 0);

  if (loading) {
    return <div className={classes.loadingWrap}><CircularProgress /></div>;
  }

  return (
    <Box p={1}>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Em aberto: <strong>{fmtMoney(totalReceber)}</strong>
      </Typography>

      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Contas a Receber</Typography>
        {receivables.length === 0 ? (
          <div className={classes.empty}>Nenhuma conta a receber.</div>
        ) : (
          <div style={{ overflowX: "auto", width: "100%" }}>
          <Table size="small" style={{ minWidth: 640 }}>
            <TableHead>
              <TableRow>
                <TableCell>Descrição</TableCell>
                <TableCell>Vencimento</TableCell>
                <TableCell align="right">Valor</TableCell>
                <TableCell align="right">Saldo</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Receber</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {receivables.map((r) => {
                const open = r.status === "pendente" || r.status === "parcial";
                return (
                  <TableRow key={r.id}>
                    <TableCell>{r.description || "—"}</TableCell>
                    <TableCell>{formatDateBR(r.dueDate)}</TableCell>
                    <TableCell align="right">{fmtMoney(r.amount)}</TableCell>
                    <TableCell align="right">{fmtMoney(r.remaining)}</TableCell>
                    <TableCell align="center">
                      <Status map={RECEIVABLE_STATUS} value={r.status} overdue={r.isOverdue} />
                    </TableCell>
                    <TableCell align="center">
                      {open && (
                        <Tooltip title="Registrar pagamento">
                          <IconButton size="small" color="primary" onClick={() => setPayTarget(r)}>
                            <PaymentIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </div>
        )}
      </div>

      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Orçamentos</Typography>
        {quotes.length === 0 ? (
          <div className={classes.empty}>Nenhum orçamento.</div>
        ) : (
          <div style={{ overflowX: "auto", width: "100%" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Data</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="center">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {quotes.map((q) => (
                <TableRow key={q.id}>
                  <TableCell>{formatDateBR(q.createdAt)}</TableCell>
                  <TableCell align="right">{fmtMoney(q.total)}</TableCell>
                  <TableCell align="center"><Status map={QUOTE_STATUS} value={q.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        )}
      </div>

      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Pagamentos</Typography>
        {payments.length === 0 ? (
          <div className={classes.empty}>Nenhum pagamento.</div>
        ) : (
          <div style={{ overflowX: "auto", width: "100%" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Data</TableCell>
                <TableCell align="right">Valor</TableCell>
                <TableCell>Forma</TableCell>
                <TableCell align="center">Recibo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>{formatDateBR(p.paidAt)}</TableCell>
                  <TableCell align="right">{fmtMoney(p.amount)}</TableCell>
                  <TableCell>{METHOD_LABELS[p.method] || p.method}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Abrir recibo">
                      <IconButton size="small" onClick={() => handleReceipt(p.id)}>
                        <ReceiptIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        )}
      </div>

      <PaymentDialog
        receivable={payTarget}
        open={!!payTarget}
        onClose={() => setPayTarget(null)}
        onPaid={() => {
          setPayTarget(null);
          fetchAll();
          toast.success("Pagamento registrado.");
        }}
      />
    </Box>
  );
};

export default PatientFinance;
